import { DarwinModal } from 'components/darwin-ui/Modal';
import styles from './index.module.css';
import { DarwinFlex, DarwinSpin } from 'components/darwin-ui';
import { useTranslation } from 'react-i18next';
import Card from './Card';
import Title from './Title';
import { useGetTransactionDetails } from 'api/services/reportsService';
import { translateFormSelect } from 'utils/common';
import { formatDate } from 'components/darwin-ui/ListView/helpers';
import { useEffect } from 'react';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { FieldKind } from 'types/reportTypes';

type TransactionDetailsModalProps = {
  open: boolean;
  onCancel: () => void;
  transactionId: string | undefined;
};

const formatters = (
  t: ReturnType<typeof useTranslation>['t']
): Record<FieldKind, (v: string) => string> => ({
  text: (v: string) => v,
  uom: (v: string) => translateFormSelect(t, v),
  date: (v: string) => formatDate(v, 'dd/MM/yyyy'),
  invoice: (v: string) => translateFormSelect(t, v),
});

export default function TransactionDetailsModal({
  open,
  onCancel,
  transactionId,
}: TransactionDetailsModalProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetTransactionDetails({
    transactionId,
    t,
  });
  const { openNotification } = useFloatNotification();
  const fns = formatters(t);

  useEffect(() => {
    if (isError) {
      openNotification(
        t('error'),
        t('reports.transaction-details-modal.loading-error'),
        'error'
      );
    }
  }, [isError, t, openNotification]);

  return (
    <DarwinModal
      className={styles.root}
      open={open}
      onCancel={() => onCancel()}
      centered
      title={<Title>{t('reports.transaction-details-modal.title')}</Title>}
      footer={null}
      width={870}
    >
      <DarwinFlex align="center" justify="center" className={styles.content}>
        {isLoading || !data ? (
          <DarwinSpin />
        ) : (
          <>
            <Card
              title={t(data.firstColumn.title)}
              items={data.firstColumn.items.map(item => ({
                key: t(item.key),
                value: fns[item.kind](item.value),
                href: item.href,
              }))}
              className={styles.primaryCard}
            />
            <section className={styles.secondColumnContent}>
              {data.secondColumn.map(
                (card, index) =>
                  card.items.length > 0 && (
                    <Card
                      key={index}
                      title={t(card.title)}
                      items={card.items.map(item => ({
                        key: t(item.key),
                        value: fns[item.kind](item.value),
                        href: item.href,
                      }))}
                    />
                  )
              )}
            </section>
          </>
        )}
      </DarwinFlex>
    </DarwinModal>
  );
}
